import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { Divider } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Updater } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  Customer,
  CustomerCreateDTO,
  ServiceTitanCustomerQueryParams,
  CustomerSchema,
  CustomerSearchDTO
} from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Box } from '_lib/components/Box/Box';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { Grid } from '_lib/components/Grid/Grid';
import { ManualErrorBoundary, RecoverableErrorState } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { MaskedPhoneDisplay } from '_lib/components/MaskedPhoneDisplay/MaskedPhoneDisplay';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { Typography } from '_lib/components/Typography/Typography';
import { CustomerForm } from '_lib/featureComponents/Customer/CustomerForm';
import { useCustomerFormDialog } from '_lib/hooks/CustomerFormDialogHook';
import {
  PrimaryHeader,
  StyledDivider,
  StyledTab,
  StyledTabs,
  PrimaryHeaderIconButton,
  ContentBox
} from '_lib/styles/PhoneCall.styled';
import { CustomerContext } from '_lib/types/PhoneCall.types';
import { isValidPhone10 } from '_lib/utils/helpers';
import { reportException } from '_lib/utils/sentryService';

import { CustomerList } from '../CustomerList/CustomerList';
import {
  CustomerSearch,
  CustomerSearchFieldEnum,
  CustomerSearchType,
  SearchableCustomerQueryParams
} from '../CustomerSearch/CustomerSearch';

import type { ApiSubmitHandler } from '_lib/types/hookForm.types';

interface CustomerSelectionProps {
  onSetCustomerContext: (customerContext: CustomerContext) => void;
  onSetCustomerSearchResults: Updater<CustomerSearchDTO[]>;
  companyId: string;
  callerId?: string;
  customers?: Customer[];
  hideAllTabs?: boolean; // This is required because nested tabs are not 'rendered', causing an error
}

export const CustomerSelection = (props: CustomerSelectionProps): React.ReactElement => {
  const { companyId, callerId, customers, onSetCustomerContext, onSetCustomerSearchResults, hideAllTabs } = props;

  const customerFormDialog = useCustomerFormDialog();
  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);

  const [customerSearch, setCustomerSearch] = useState<CustomerSearchType>({
    fieldName: CustomerSearchFieldEnum.phoneNumber,
    text: callerId || ''
  });

  const [isSavingOrLoading, setIsSavingOrLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const [validationErrors, setValidationErrors] = useState<string[] | undefined>(undefined);
  const [recoverableSearchError, setRecoverableSearchError] = useState<RecoverableErrorState | null>(null);

  const handleCustomerSelected = useCallback(
    async (customer: Customer) => {
      try {
        setIsSavingOrLoading(true);

        const [customerIdResponse, locationsResponse] = await Promise.all([
          serviceTitanApi.customers.getById({ id: customer.providerId }),
          serviceTitanApi.locations.allCustomerLocations({ params: { providerCustomerId: customer.providerId } })
        ]);

        const { notes, contacts, ...baseCustomer } = customerIdResponse;

        const parsedCustomer = CustomerSchema.safeParse(baseCustomer);
        if (!parsedCustomer.success) {
          const result = await customerFormDialog.show({
            customer: baseCustomer,
            customerResource: serviceTitanApi.customers
          });
          if (!result || result.action === 'CANCEL') return;
          else {
            onSetCustomerContext({
              customer: { notes, contacts, ...result.value.customer },
              locations: locationsResponse.results ?? []
            });
          }
        } else {
          onSetCustomerContext({
            customer: customerIdResponse,
            locations: locationsResponse.results ?? []
          });
        }
      } catch (err) {
        setRecoverableSearchError({
          error: err,
          message: 'Failed to select customer - Please search again or create a new customer',
          shouldReport: true
        });
      } finally {
        setIsSavingOrLoading(false);
      }
    },
    [onSetCustomerContext, serviceTitanApi.customers, serviceTitanApi.locations, customerFormDialog]
  );

  const handleCustomerFormCreate: ApiSubmitHandler<CustomerCreateDTO> = useCallback(
    async (formCustomer: CustomerCreateDTO, { setFormErrors, resetForm }) => {
      try {
        setValidationErrors(undefined);
        setIsSavingOrLoading(true);
        const createdCustomer = await serviceTitanApi.customers.create({ ...formCustomer });
        const { locations, ...customerDetailDto } = createdCustomer;

        onSetCustomerContext({
          customer: customerDetailDto,
          locations: locations
        });

        // Reset form and make sure we go back to the list after creating a new customer
        resetForm?.();
        setTabIndex(0);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setFormErrors(err);
          setValidationErrors(err.responseData?.nonFieldErrors);
        }

        reportException(err);
      } finally {
        setIsSavingOrLoading(false);
      }
    },
    [onSetCustomerContext, serviceTitanApi.customers]
  );

  const handleCustomerSearch = async (search: CustomerSearchType) => {
    setRecoverableSearchError(null);
    setCustomerSearch(search);
    const params: ServiceTitanCustomerQueryParams = {
      [search.fieldName as keyof SearchableCustomerQueryParams]: search.text
    };

    try {
      setIsSavingOrLoading(true);
      const customersResponse = await serviceTitanApi.customers.search({ params });
      onSetCustomerSearchResults(customersResponse.results);
    } catch (err) {
      setRecoverableSearchError({
        error: err,
        message: 'Failed to load Customers - Please Try again',
        shouldReport: true
      });
    } finally {
      setIsSavingOrLoading(false);
    }
  };

  /**
   * Standard customer setter and initial loading useEffect
   */
  const isInitializing = useRef(true);
  useEffect(() => {
    const fetchCustomerListOnInit = async (callPopCallerId: string) => {
      try {
        setIsSavingOrLoading(true);
        setCustomerSearch({ fieldName: CustomerSearchFieldEnum.phoneNumber, text: callPopCallerId });
        const customersResponse = await serviceTitanApi.customers.search({ params: { phoneNumber: callerId } });
        onSetCustomerSearchResults(customersResponse.results);

        /**
         * If no customers were found with the initial phoneNumber search,
         * default the customer search to be addressLine1
         */
        if (!customersResponse?.results.length) {
          setCustomerSearch({
            fieldName: CustomerSearchFieldEnum.addressLine1,
            text: ''
          });
        } else if (customersResponse.results.length === 1) {
          const singleCustomer = customersResponse.results[0];
          if (singleCustomer) await handleCustomerSelected(singleCustomer);
        }
      } catch (err) {
        setRecoverableSearchError({
          error: err,
          message: 'Failed to load Customers - Please Try again',
          shouldReport: true
        });
      } finally {
        setIsSavingOrLoading(false);
      }
    };

    if (isInitializing.current && isValidPhone10(callerId)) {
      isInitializing.current = false;
      fetchCustomerListOnInit(callerId);
    }
  }, [
    callerId,
    customerSearch.fieldName,
    customerSearch.text,
    handleCustomerSelected,
    onSetCustomerSearchResults,
    serviceTitanApi.customers
  ]);

  /**
   * Company Switch useEffect
   */
  const lastSelectedCompanyId = useRef(companyId);
  useEffect(() => {
    const fetchCustomerListOnCompanySwitch = async () => {
      lastSelectedCompanyId.current = companyId;

      /**
       * Do NOT search if we do not have anything in our search field
       *
       * This is needed because our fetcher here is not guarded by form validation, as it
       * does not rely on user input
       */
      if (!customerSearch.text) {
        onSetCustomerSearchResults([]);
        return;
      }

      const params: ServiceTitanCustomerQueryParams = {
        [customerSearch.fieldName as keyof SearchableCustomerQueryParams]: customerSearch.text
      };
      try {
        setIsSavingOrLoading(true);
        const customersResponse = await serviceTitanApi.customers.search({ params });
        onSetCustomerSearchResults(customersResponse.results);

        if (customersResponse.results.length === 1) {
          const singleCustomer = customersResponse.results[0];
          if (singleCustomer) await handleCustomerSelected(singleCustomer);
        }
      } catch (err) {
        onSetCustomerSearchResults([]);
        setRecoverableSearchError({
          error: err,
          message: 'Failed to load Customers - Please Try again',
          shouldReport: true
        });
      } finally {
        setIsSavingOrLoading(false);
      }
    };

    if (!isInitializing.current && companyId !== lastSelectedCompanyId.current) fetchCustomerListOnCompanySwitch();
  }, [
    companyId,
    customerSearch.fieldName,
    customerSearch.text,
    handleCustomerSelected,
    onSetCustomerSearchResults,
    serviceTitanApi.customers
  ]);

  return (
    <>
      <PrimaryHeader>
        <Grid container sx={{ paddingX: 1.5, paddingY: 1 }}>
          <Grid item xs={10}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant='h3' text='Unknown' />
              <MaskedPhoneDisplay variant='h3' preText='CID: ' text={callerId ?? 'Invalid Caller ID'} />
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexWrap: 'nowrap',
                flexDirection: 'row',
                width: '100%'
              }}>
              {tabIndex === 1 && (
                <>
                  <PrimaryHeaderIconButton
                    disabled={isSavingOrLoading}
                    key={'customer-not-selected-customer-form-submit'}
                    type='submit'
                    form='st-api-call-pop-customer-form'
                    size='small'>
                    <SaveIcon />
                  </PrimaryHeaderIconButton>
                  <PrimaryHeaderIconButton onClick={() => setTabIndex(0)} disabled={isSavingOrLoading} size='small'>
                    <CloseIcon />
                  </PrimaryHeaderIconButton>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        <StyledDivider />
        {!hideAllTabs && (
          <StyledTabs
            value={tabIndex}
            onChange={(_event: React.SyntheticEvent, newValue: number) => setTabIndex(newValue)}>
            <StyledTab label={'SEARCH'} id='simple-tab-0' />
            <StyledTab label={'NEW CUSTOMER'} id='simple-tab-1' />
          </StyledTabs>
        )}
      </PrimaryHeader>
      <TabPanel currentIndex={tabIndex} index={0}>
        <ContentBox>
          <BackdropSpinner open={isSavingOrLoading} />
          {validationErrors?.length && <ErrorDisplay errors={validationErrors} />}
          <CustomerSearch
            onCustomerSearch={handleCustomerSearch}
            searchField={customerSearch.fieldName}
            searchText={customerSearch.text}
          />
          <Divider />
          {!recoverableSearchError && (
            <CustomerList customerList={customers} onCustomerClicked={handleCustomerSelected} />
          )}
          {recoverableSearchError && (
            <ManualErrorBoundary
              errorState={recoverableSearchError}
              reportingCtx={{ extra: { customerSearch } }}
              sx={{ justifyContent: 'flex-start', paddingTop: 6 }}
            />
          )}
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={1}>
        <ContentBox>
          <BackdropSpinner open={isSavingOrLoading} />
          {validationErrors?.length && <ErrorDisplay errors={validationErrors} />}
          <CustomerForm onCustomerSubmit={handleCustomerFormCreate} />
        </ContentBox>
      </TabPanel>
      {customerFormDialog.element}
    </>
  );
};
